/**
 * GM Missive Reports index page.
 * Lists all missive reports with status filtering.
 */

import { Link, router } from '@inertiajs/react';
import GmLayout from '@/layouts/GmLayout';
import { FlashMessage } from '@/components/ui/FlashMessage';

/** Report data from the backend */
interface ReportData {
    id: number;
    reason: string;
    status: { value: string; label: string };
    missive: {
        id: number;
        subject: string;
        sender: { id: number; username: string } | null;
        recipient: { id: number; username: string } | null;
    };
    reporter: { id: number; username: string };
    reviewer: { id: number; username: string } | null;
    created_at: string;
    reviewed_at: string | null;
}

/** Pagination link from Laravel */
interface PaginationLink {
    url: string | null;
    label: string;
    active: boolean;
}

/** Paginated response from Laravel */
interface PaginatedReports {
    data: ReportData[];
    current_page: number;
    last_page: number;
    per_page: number;
    total: number;
    links: PaginationLink[];
}

/** Status counts */
interface StatusCounts {
    pending: number;
    reviewed: number;
    resolved: number;
    dismissed: number;
}

interface Props {
    reports: PaginatedReports;
    status: string | null;
    statusCounts: StatusCounts;
}

/** Format date in a readable format */
function formatDate(isoString: string): string {
    return new Date(isoString).toLocaleDateString(undefined, {
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
    });
}

/** Status badge colors */
const STATUS_COLORS: Record<string, string> = {
    pending: 'bg-yellow-600',
    reviewed: 'bg-blue-600',
    resolved: 'bg-green-600',
    dismissed: 'bg-slate-600',
};

/**
 * GM Missive Reports Index page.
 */
export default function Index({ reports, status, statusCounts }: Props) {
    /** Handle status filter change */
    const handleStatusChange = (newStatus: string | null) => {
        router.get('/gm/missive-reports', newStatus ? { status: newStatus } : {}, {
            preserveState: true,
        });
    };

    return (
        <GmLayout title="Missive Reports" activePath="/gm/missive-reports">
            <FlashMessage />

            <div className="p-6">
                {/* Header */}
                <div className="flex items-center justify-between mb-6">
                    <h1 className="text-2xl font-bold text-white">🚩 Missive Reports</h1>
                    <Link
                        href="/gm/missive-reports/all-missives"
                        className="px-4 py-2 bg-indigo-600 hover:bg-indigo-500 text-white text-sm font-medium rounded-lg transition-colors"
                    >
                        Browse All Missives
                    </Link>
                </div>

                {/* Status Filters */}
                <div className="flex gap-2 mb-6">
                    <FilterButton
                        label="All"
                        count={statusCounts.pending + statusCounts.reviewed + statusCounts.resolved + statusCounts.dismissed}
                        isActive={!status}
                        onClick={() => handleStatusChange(null)}
                    />
                    <FilterButton
                        label="Pending"
                        count={statusCounts.pending}
                        isActive={status === 'pending'}
                        onClick={() => handleStatusChange('pending')}
                        color="yellow"
                    />
                    <FilterButton
                        label="Reviewed"
                        count={statusCounts.reviewed}
                        isActive={status === 'reviewed'}
                        onClick={() => handleStatusChange('reviewed')}
                        color="blue"
                    />
                    <FilterButton
                        label="Resolved"
                        count={statusCounts.resolved}
                        isActive={status === 'resolved'}
                        onClick={() => handleStatusChange('resolved')}
                        color="green"
                    />
                    <FilterButton
                        label="Dismissed"
                        count={statusCounts.dismissed}
                        isActive={status === 'dismissed'}
                        onClick={() => handleStatusChange('dismissed')}
                    />
                </div>

                {/* Reports Table */}
                {reports.data.length === 0 ? (
                    <div className="text-center py-12 text-slate-400 bg-slate-800 rounded-lg">
                        <span className="text-3xl mb-2 block">✓</span>
                        <p>No reports found.</p>
                    </div>
                ) : (
                    <div className="bg-slate-800 rounded-lg border border-slate-700 overflow-hidden">
                        <table className="w-full">
                            <thead className="bg-slate-700">
                                <tr>
                                    <th className="px-4 py-3 text-left text-sm font-medium text-slate-300">Status</th>
                                    <th className="px-4 py-3 text-left text-sm font-medium text-slate-300">Missive</th>
                                    <th className="px-4 py-3 text-left text-sm font-medium text-slate-300">Reporter</th>
                                    <th className="px-4 py-3 text-left text-sm font-medium text-slate-300">Date</th>
                                    <th className="px-4 py-3 text-right text-sm font-medium text-slate-300">Actions</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-slate-700">
                                {reports.data.map((report) => (
                                    <ReportRow key={report.id} report={report} />
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}

                {/* Pagination */}
                {reports.last_page > 1 && (
                    <div className="mt-6 flex justify-center gap-1">
                        {reports.links.map((link, index) => (
                            <a
                                key={index}
                                href={link.url || '#'}
                                className={`px-3 py-1 rounded text-sm ${
                                    link.active
                                        ? 'bg-amber-600 text-white'
                                        : link.url
                                        ? 'bg-slate-700 text-gray-300 hover:bg-slate-600'
                                        : 'bg-slate-800 text-gray-500 cursor-not-allowed'
                                }`}
                                dangerouslySetInnerHTML={{ __html: link.label }}
                            />
                        ))}
                    </div>
                )}
            </div>
        </GmLayout>
    );
}

/** Filter button component */
function FilterButton({
    label,
    count,
    isActive,
    onClick,
    color,
}: {
    label: string;
    count: number;
    isActive: boolean;
    onClick: () => void;
    color?: 'yellow' | 'blue' | 'green';
}) {
    const colorClasses = {
        yellow: 'bg-yellow-600',
        blue: 'bg-blue-600',
        green: 'bg-green-600',
    };

    return (
        <button
            onClick={onClick}
            className={`px-4 py-2 rounded-lg font-medium transition-colors flex items-center gap-2 ${
                isActive
                    ? 'bg-amber-600 text-white'
                    : 'bg-slate-700 text-slate-300 hover:bg-slate-600'
            }`}
        >
            {label}
            <span className={`px-1.5 py-0.5 text-xs rounded-full ${
                color && !isActive ? colorClasses[color] : 'bg-white/20'
            }`}>
                {count}
            </span>
        </button>
    );
}

/** Report row component */
function ReportRow({ report }: { report: ReportData }) {
    return (
        <tr className="hover:bg-slate-700/50">
            <td className="px-4 py-3">
                <span className={`px-2 py-1 text-xs rounded-full text-white ${
                    STATUS_COLORS[report.status.value] || 'bg-slate-600'
                }`}>
                    {report.status.label}
                </span>
            </td>
            <td className="px-4 py-3">
                <div className="text-white font-medium truncate max-w-xs">
                    {report.missive.subject}
                </div>
                <div className="text-sm text-slate-400">
                    {report.missive.sender?.username || 'Unknown'} → {report.missive.recipient?.username || 'Unknown'}
                </div>
            </td>
            <td className="px-4 py-3 text-slate-300">
                {report.reporter.username}
            </td>
            <td className="px-4 py-3 text-slate-400 text-sm">
                {formatDate(report.created_at)}
            </td>
            <td className="px-4 py-3 text-right">
                <Link
                    href={`/gm/missive-reports/${report.id}`}
                    className="px-3 py-1 bg-slate-600 hover:bg-slate-500 rounded text-sm transition-colors"
                >
                    View
                </Link>
            </td>
        </tr>
    );
}
